import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Clock,
  Calendar,
  Upload,
  CheckCircle,
  AlertCircle,
  Building,
  Briefcase,
  X,
  Send,
  Download
} from 'lucide-react';
import axios from 'axios';
import { apiErrorText } from '../../utils/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface CaseStudyData {
  id: number;
  title: string;
  description: string | null;
  expected_deliverables: string[];
  attachment_files: {name: string; url: string; size: number}[];
  due_at: string;
  allow_late_submission: boolean;
  status: string;
  submitted_at: string | null;
  company_name: string | null;
  position_title: string | null;
  language?: string | null;
}

interface UploadedFile {
  name: string;
  url: string;
  size: number;
}

const CaseStudySubmit: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [caseStudy, setCaseStudy] = useState<CaseStudyData | null>(null);
  const [submissionText, setSubmissionText] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadCaseStudy();
  }, [token]);

  const loadCaseStudy = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/public/case-study/${token}`);
      const data: CaseStudyData = response.data;

      // Switch UI language to match case study content language (if provided)
      if (data.language === 'tr' || data.language === 'en') {
        if (i18n.language !== data.language) {
          i18n.changeLanguage(data.language);
        }
      }

      setCaseStudy(data);
      
      // Mark as started if pending
      if (data.status === 'pending') {
        await axios.post(`${API_URL}/api/public/case-study/${token}/start`);
      }
    } catch (err: any) {
      setError(apiErrorText(err.response?.data?.detail) || t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingFile(true);
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);

        // Upload to backend
        const response = await axios.post(`${API_URL}/api/upload/public`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        setUploadedFiles(prev => [...prev, {
          name: file.name,
          url: response.data.url,
          size: file.size
        }]);
      }
    } catch (err: any) {
      console.error('File upload failed:', err);
      setError(t('errors.uploadFailed'));
    } finally {
      setUploadingFile(false);
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!submissionText.trim() && uploadedFiles.length === 0) {
      setError(t('caseStudy.submitRequired'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API_URL}/api/public/case-study/${token}/submit`, {
        submission_text: submissionText,
        submission_files: uploadedFiles
      });
      setSuccess(true);
    } catch (err: any) {
      setError(apiErrorText(err.response?.data?.detail) || t('errors.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOverdue = caseStudy ? new Date(caseStudy.due_at) < new Date() : false;
  const canSubmit = caseStudy && 
    (caseStudy.status === 'pending' || caseStudy.status === 'in_progress') &&
    (!isOverdue || caseStudy.allow_late_submission);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (error && !caseStudy) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {t('caseStudy.notFound')}
          </h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-600" size={40} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('caseStudy.submitSuccess')}
          </h1>
          <p className="text-gray-600 mb-6">
            {t('caseStudy.submitSuccessMessage')}
          </p>
          <div className="text-sm text-gray-500">
            {caseStudy?.company_name && (
              <p>{caseStudy.company_name}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (caseStudy?.status === 'submitted' || caseStudy?.status === 'evaluated') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle className="text-green-500 mx-auto mb-4" size={48} />
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {t('caseStudy.alreadySubmitted')}
          </h1>
          <p className="text-gray-600">
            {t('caseStudy.alreadySubmittedMessage')}
          </p>
          {caseStudy.submitted_at && (
            <p className="text-sm text-gray-500 mt-4">
              {t('caseStudy.submittedAt')}: {formatDate(caseStudy.submitted_at)}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <FileText className="text-amber-600" size={24} />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                {caseStudy?.title}
              </h1>
              <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                {caseStudy?.company_name && (
                  <span className="flex items-center gap-1">
                    <Building size={14} />
                    {caseStudy.company_name}
                  </span>
                )}
                {caseStudy?.position_title && (
                  <span className="flex items-center gap-1">
                    <Briefcase size={14} />
                    {caseStudy.position_title}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Deadline */}
          <div className={`mt-4 p-4 rounded-lg ${isOverdue ? 'bg-red-50' : 'bg-amber-50'}`}>
            <div className="flex items-center gap-2">
              <Calendar className={isOverdue ? 'text-red-600' : 'text-amber-600'} size={18} />
              <span className={`font-medium ${isOverdue ? 'text-red-700' : 'text-amber-700'}`}>
                {t('caseStudy.deadline')}:
              </span>
              <span className={isOverdue ? 'text-red-600' : 'text-amber-600'}>
                {caseStudy?.due_at && formatDate(caseStudy.due_at)}
              </span>
            </div>
            {isOverdue && !caseStudy?.allow_late_submission && (
              <p className="text-red-600 text-sm mt-2">
                {t('caseStudy.deadlinePassed')}
              </p>
            )}
            {isOverdue && caseStudy?.allow_late_submission && (
              <p className="text-amber-600 text-sm mt-2">
                {t('caseStudy.lateAllowed')}
              </p>
            )}
          </div>
        </div>

        {/* Description */}
        {caseStudy?.description && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">
              {t('caseStudy.description')}
            </h2>
            <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
              {caseStudy.description}
            </div>
          </div>
        )}

        {/* Expected Deliverables */}
        {caseStudy?.expected_deliverables && caseStudy.expected_deliverables.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3">
              {t('caseStudy.expectedDeliverables')}
            </h2>
            <ul className="space-y-2">
              {caseStudy.expected_deliverables.map((item, index) => (
                <li key={index} className="flex items-start gap-2">
                  <CheckCircle className="text-amber-500 mt-0.5 flex-shrink-0" size={16} />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Attachment Files (downloadable by candidate) */}
        {caseStudy?.attachment_files && caseStudy.attachment_files.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Download size={20} className="text-amber-600" />
              {t('caseStudy.downloadableFiles')}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {t('caseStudy.downloadableFilesHint')}
            </p>
            <div className="space-y-2">
              {caseStudy.attachment_files.map((file, index) => (
                <a
                  key={index}
                  href={file.url.startsWith('/') ? `${API_URL}${file.url}` : file.url}
                  download={file.name}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-lg p-4 transition-colors border border-gray-200"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="text-amber-600" size={20} />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <Download size={18} className="text-gray-400" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Submission Form */}
        {canSubmit && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              {t('caseStudy.yourSubmission')}
            </h2>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-3">
                <AlertCircle className="text-red-600" size={20} />
                <p className="text-red-700">{error}</p>
              </div>
            )}

            {/* Text Response */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('caseStudy.writtenResponse')}
              </label>
              <textarea
                value={submissionText}
                onChange={(e) => setSubmissionText(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 min-h-[200px]"
                placeholder={t('caseStudy.responsePlaceholder')}
              />
            </div>

            {/* File Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('caseStudy.attachFiles')}
              </label>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-amber-500 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.txt,.md"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  {uploadingFile ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
                  ) : (
                    <>
                      <Upload className="text-gray-400 mx-auto mb-2" size={32} />
                      <p className="text-gray-600">
                        {t('caseStudy.dropFiles')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, ZIP
                      </p>
                    </>
                  )}
                </label>
              </div>

              {/* Uploaded Files List */}
              {uploadedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <FileText className="text-amber-600" size={20} />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{file.name}</p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <X size={16} className="text-gray-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={submitting || (!submissionText.trim() && uploadedFiles.length === 0)}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  {t('common.submitting')}
                </>
              ) : (
                <>
                  <Send size={18} />
                  {t('caseStudy.submit')}
                </>
              )}
            </button>
          </div>
        )}

        {/* Cannot Submit Message */}
        {!canSubmit && caseStudy?.status !== 'submitted' && caseStudy?.status !== 'evaluated' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
            <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {t('caseStudy.cannotSubmit')}
            </h2>
            <p className="text-gray-600">
              {isOverdue 
                ? t('caseStudy.deadlinePassedMessage')
                : t('caseStudy.submissionClosed')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseStudySubmit;
